import { ValidationError } from 'yup';
import { assetTypeImportRowRules } from '../../store/modules/asset/asset-type.validation';
import { IImportSummary, IPreviewRow, TRowStatus } from './i-asset-type-import';

export const ROW_LEVEL_KEY = 'row';

const TAB_OR_NEWLINE = /[\t\r\n]/;

export interface IRowIssues {
  errors: Record<string, string>;
  warnings: Record<string, string>;
}

export const validateRow = (row: IPreviewRow): IRowIssues => {
  const errors: Record<string, string> = {};
  const warnings: Record<string, string> = {};

  try {
    assetTypeImportRowRules.validateSync(
      { code: row.code, description: row.description },
      { abortEarly: false },
    );
  } catch (err) {
    const failure = err as ValidationError;
    const issues = failure.inner.length > 0 ? failure.inner : [failure];
    issues.forEach((issue) => {
      if (issue.path && !errors[issue.path]) {
        errors[issue.path] = issue.message;
      }
    });
  }

  if (!errors.code && row.code.trim().startsWith('#')) {
    errors.code = 'Code cannot start with "#" — the importer would skip this line';
  }

  if (TAB_OR_NEWLINE.test([row.code, row.description, ...row.props].join(''))) {
    warnings[ROW_LEVEL_KEY] = 'Tabs and line breaks will be replaced with spaces on import';
  }

  return { errors, warnings };
};

export const validateImportRows = (
  rows: IPreviewRow[],
  existingCodes: Set<string> | null,
): { rows: IPreviewRow[]; summary: IImportSummary } => {
  const firstSeenAtLine = new Map<string, number>();

  const validated = rows.map((row) => {
    const { errors, warnings } = validateRow(row);
    const key = row.code.trim().toLowerCase();

    if (key !== '') {
      const earlier = firstSeenAtLine.get(key);
      if (earlier === undefined) {
        firstSeenAtLine.set(key, row.line);
      } else {
        errors[ROW_LEVEL_KEY] = `Duplicate code in this file (line ${earlier})`;
      }
    }

    let status: TRowStatus = 'new';
    if (Object.keys(errors).length > 0) {
      status = 'invalid';
    } else if (existingCodes !== null && existingCodes.has(key)) {
      status = 'duplicate';
      warnings[ROW_LEVEL_KEY] = 'Already exists in this tenant';
    }

    return {
      ...row, status, errors, warnings,
    };
  });

  const countOf = (status: TRowStatus): number => (
    validated.filter((row) => row.status === status).length
  );

  return {
    rows: validated,
    summary: {
      total: validated.length,
      newCount: countOf('new'),
      duplicateCount: countOf('duplicate'),
      invalidCount: countOf('invalid'),
    },
  };
};
